import { useCallback, useEffect, useRef, useState } from 'react';
import type { ThemePackageView, ThemeValidationView } from '@icetus/contracts';
import { composeThemeManifest, readThemePackage, type ThemeManifest } from '@icetus/theme-kit';
import { Badge, Button, Card, formatMoney } from '@icetus/ui';
import { openThemeZip } from '../lib/theme-zip';
import {
  ApiError,
  fetchThemePackages,
  publishTheme,
  restoreTheme,
  retireTheme,
  uploadThemePreview,
  validateThemePackage,
} from '../api';

/**
 * 主题发布（平台超管）。
 *
 * 放在主题市场页里而不是独立一级菜单：发布是**低频动作**，一年也用不了几次，
 * 占一个导航项只会让客户看的菜单里多一条他永远点不进去的东西。
 *
 * 四条交互取舍：
 *
 *   1) **只收一个压缩包**。主题包是 4 份 JSON + 2 张图，多选文件很容易漏一份，
 *      而漏掉的症状不是报错 —— `composeThemeManifest` 会安静地少合并一块，
 *      于是上架的主题没有变体、或者没有页面模板，谁也不会立刻发现。
 *   2) **选完文件立刻校验**，不等点发布。员工改一次配色就想知道过不过，
 *      让他"点发布 → 失败 → 看一条错误 → 再点"是最容易被放弃的节奏。
 *   3) 错误**一次列全**，不是遇到第一条就停 —— 一条一条改比一次改完慢十倍。
 *   4) 认内容不认文件名：工坊可能把 theme.json 改名，按文件名判断会
 *      冤枉一份完整的包。
 */


/**
 * 两张预览图的规格。
 *
 * 与 `@icetus/theme-kit` 的 `THEME_PREVIEW_SPEC` 是同一组数字 —— 这里抄一份
 * 是为了**上传前就拦下明显不对的文件**（省一次白传 800KB），服务端仍然是
 * 唯一的判据。抄歪了的后果只是前端提示不准，不会让不合规的图进市场。
 */
const PREVIEW_SLOTS = [
  {
    kind: 'card' as const,
    label: '市场卡片图',
    file: 'preview.png',
    width: 800,
    height: 500,
    maxKb: 400,
    hint: '市场列表里那张。卡片实际只显示 210–320px 宽，800 宽已经覆盖 2x 屏——再大只是被浏览器缩掉。',
  },
  {
    kind: 'hero' as const,
    label: '详情大图',
    file: 'preview-hero.png',
    width: 1600,
    height: 1000,
    maxKb: 800,
    hint: '客户点开卡片看到的那张。这张才是说服他下单的图，允许更大的体积。',
  },
];

function readImageSize(file: File): Promise<{ w: number; h: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ w: img.naturalWidth, h: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('读不出这张图的尺寸，文件可能坏了'));
    };
    img.src = url;
  });
}

export function ThemePublishPanel() {
  const [manifest, setManifest] = useState<ThemeManifest | null>(null);
  const [verdict, setVerdict] = useState<ThemeValidationView | null>(null);
  const [pickedNames, setPickedNames] = useState<string[]>([]);
  /** 包里读到了什么（图与没有认出来的文件），选完就展示，省得人猜 */
  const [zipSummary, setZipSummary] = useState<{
    card?: string;
    hero?: string;
    others: string[];
    ignored: string[];
  } | null>(null);
  const [busy, setBusy] = useState<'zip' | 'validate' | 'publish' | string | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [list, setList] = useState<ThemePackageView[]>([]);
  const [previewUrls, setPreviewUrls] = useState<{ card?: string; hero?: string }>({});
  const [previewInfo, setPreviewInfo] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState<'card' | 'hero' | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const loadList = useCallback(async () => {
    try {
      const res = await fetchThemePackages();
      setList(res.items);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  /**
   * 选一个压缩包 → 拆包 → 合并 → 校验 → 顺手把包里的图传上去。
   *
   * 校验与传图**并行**：校验是纯计算（几十毫秒），传图是两次网络往返。
   * 串起来只是让人多等，没有任何收益。
   */
  const onPickZip = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    setError('');
    setNotice('');
    setVerdict(null);
    setManifest(null);
    setPickedNames([]);
    setZipSummary(null);
    setPreviewUrls({});
    setPreviewInfo({});
    setBusy('zip');

    try {
      const opened = await openThemeZip(file);
      setPickedNames(opened.json.map((f) => f.name));
      setZipSummary({
        card: opened.card?.name,
        hero: opened.hero?.name,
        others: opened.otherImages,
        ignored: opened.ignored,
      });

      const composed = composeThemeManifest(readThemePackage(opened.json));
      setManifest(composed);

      /* 重新发布时包里可以不带图：manifest 里已经记着上次那张，下面会显示出来 */
      const existing = (composed as { preview?: { card?: string; hero?: string } }).preview;
      setPreviewUrls({ card: existing?.card, hero: existing?.hero });

      setBusy('validate');
      /**
       * 校验与传图一起等。
       *
       * 不能先 `const pending = validate(...)` 再去 await 上传：上传要几秒，
       * 而 pending 若在这期间 reject，浏览器会报 unhandled rejection（页面不会崩，
       * 但控制台一片红）。`allSettled` 把两条路都收回同一个出口。
       */
      const uploads: Array<Promise<void>> = [];
      if (opened.card) uploads.push(uploadPreview('card', opened.card.file));
      if (opened.hero) uploads.push(uploadPreview('hero', opened.hero.file));

      const [validation] = await Promise.allSettled([validateThemePackage(composed), ...uploads]);
      if (validation!.status === 'fulfilled') {
        setVerdict(validation!.value);
      } else {
        const reason = validation!.reason;
        setError(reason instanceof ApiError ? reason.message : String(reason));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  /**
   * 把关 → 直传对象存储 → 记住地址。包里的图与手动补传共用这一段。
   *
   * 本地把关只做两件不需要服务端的事：类型与体积。尺寸不拦（只提示），
   * 因为"800×500"是我们对卡片布局的判断，而服务端本来就只按体积与类型收货 ——
   * 前端硬拦一个服务端不拦的规则，会让"我明明能传上去"变成"你这里不让传"。
   */
  const uploadPreview = async (kind: 'card' | 'hero', file: File): Promise<void> => {
    const slot = PREVIEW_SLOTS.find((s) => s.kind === kind)!;

    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      setError(`「${slot.label}」只收 PNG / JPG / WebP，包里这张是「${file.type || '未知类型'}」`);
      return;
    }
    if (file.size > slot.maxKb * 1024) {
      setError(
        `「${slot.label}」${Math.round(file.size / 1024)}KB，超过 ${slot.maxKb}KB。` +
          `先把它压到 ${slot.width}×${slot.height} 再过一遍调色板降色，通常就够了。`,
      );
      return;
    }

    setUploading(kind);
    try {
      const [{ w, h }, url] = await Promise.all([readImageSize(file), uploadThemePreview(file, kind)]);
      setPreviewUrls((prev) => ({ ...prev, [kind]: url }));
      setPreviewInfo((prev) => ({
        ...prev,
        [kind]: `${w}×${h} · ${Math.round(file.size / 1024)}KB`,
      }));

      /* 尺寸不对只说一句，不拦 —— 服务端收的是体积与类型，会不会被裁由卡片决定 */
      if (w !== slot.width || h !== slot.height) {
        setError(
          `「${slot.label}」是 ${w}×${h}，建议 ${slot.width}×${slot.height}。` +
            `不影响上传，但上架后会被卡片裁掉一部分。`,
        );
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setUploading(null);
    }
  };

  /** 单独补一张图（包里没带，或者只想换掉其中一张） */
  const onPickPreview = async (kind: 'card' | 'hero', file: File | undefined): Promise<void> => {
    if (!file) return;
    setError('');
    setNotice('');
    await uploadPreview(kind, file);
  };

  const onPublish = async (): Promise<void> => {
    if (!manifest) return;
    setBusy('publish');
    setError('');
    setNotice('');
    try {
      const preview =
        previewUrls.card || previewUrls.hero
          ? { card: previewUrls.card, hero: previewUrls.hero }
          : undefined;
      const res = await publishTheme(manifest, preview);
      setNotice(
        `「${res.theme.label.zh}」已发布（${res.theme.variantCount} 个变体）。去下面的市场里就能看到，客户可以直接订阅。`,
      );
      setVerdict(null);
      setManifest(null);
      setPickedNames([]);
      setZipSummary(null);
      setPreviewUrls({});
      setPreviewInfo({});
      if (fileInput.current) fileInput.current.value = '';
      await loadList();
    } catch (err) {
      // 校验失败的 details 里是逐条原因，接口层已经把 message 汇总成一句
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const onToggleStatus = async (item: ThemePackageView): Promise<void> => {
    setBusy(item.name);
    setError('');
    setNotice('');
    try {
      if (item.status === 'published') {
        await retireTheme(item.name);
        setNotice(`「${item.label.zh}」已下架：不再出现在客户的市场里，已订阅的客户仍可用到期满。`);
      } else {
        await restoreTheme(item.name);
        setNotice(`「${item.label.zh}」已重新上架。`);
      }
      await loadList();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="stack">
      <Card
        title="发布主题（平台管理员）"
        description="把主题文件夹压成一个 zip 传上来，选完立刻校验，通过后一键上架。市场里每套主题都必须带卡片图 —— 客户是靠那张图决定点不点进来的。"
      >
        {notice ? <div className="notice notice--ok">{notice}</div> : null}
        {error ? <div className="notice notice--danger">{error}</div> : null}

        <div className="theme-publish__pick">
          <input
            ref={fileInput}
            type="file"
            accept=".zip,application/zip"
            onChange={(e) => void onPickZip(e.target.files?.[0])}
          />
          {zipSummary ? (
            <div className="theme-publish__bag">
              <p className="muted">
                包里读到 <strong>{pickedNames.length}</strong> 份配置：{pickedNames.join('、')}
              </p>
              <p className="muted">
                卡片图：
                {zipSummary.card ? (
                  <span className="ok-text">{zipSummary.card}</span>
                ) : (
                  <span className="bad-text">包里没有 preview.png</span>
                )}
                {' · '}
                详情图：{zipSummary.hero ? zipSummary.hero : '没带（可选）'}
              </p>
              {zipSummary.others.length > 0 ? (
                <p className="muted">
                  另外还有 {zipSummary.others.length} 张图没用到：
                  {zipSummary.others.join('、')}
                </p>
              ) : null}
              {zipSummary.ignored.length > 0 ? (
                <p className="muted">
                  跳过 {zipSummary.ignored.length} 个非主题文件：{zipSummary.ignored.join('、')}
                </p>
              ) : null}
            </div>
          ) : (
            <p className="muted">
              把主题文件夹压成一个 <code>.zip</code> 传上来 —— 里面放 theme.json /
              tokens.json / variants.json / layout.json / preview.png / preview-hero.png。
              一个包一次传完，不会漏。
            </p>
          )}
        </div>

        {busy === 'zip' ? <p className="muted">拆包中…</p> : null}
        {busy === 'validate' ? <p className="muted">校验中…</p> : null}

        {manifest ? (
          <div className="theme-preview-pick">
            <p className="muted">
              这两张图决定客户在市场上第一眼看到什么。<strong>卡片图必填</strong>
              —— 市场里没有缩略图的主题，客户根本不会点进去。包里带的图会自动传上来，
              也可以在这里单独换掉其中一张。
            </p>
            <div className="theme-preview-pick__grid">
              {PREVIEW_SLOTS.map((slot) => (
                <div key={slot.kind} className="theme-preview-pick__slot">
                  <span className="theme-preview-pick__title">
                    {slot.label}
                    <span className="muted">
                      {' '}
                      {slot.width}×{slot.height} · ≤{slot.maxKb}KB
                    </span>
                  </span>
                  {previewUrls[slot.kind] ? (
                    <img
                      className="theme-preview-pick__img"
                      src={previewUrls[slot.kind]}
                      alt={slot.label}
                    />
                  ) : (
                    <span className="theme-preview-pick__empty">还没选图</span>
                  )}
                  <input
                    type="file"
                    accept="image/png,image/webp"
                    onChange={(e) => void onPickPreview(slot.kind, e.target.files?.[0])}
                  />
                  <span className="muted">
                    {uploading === slot.kind ? '上传中…' : (previewInfo[slot.kind] ?? slot.hint)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {verdict ? (
          <div className="theme-publish__verdict">
            <p>
              <strong>{verdict.ok ? '校验通过' : '校验未通过'}</strong>
              <span className="muted">
                {' '}
                · {verdict.tokenCount} 条令牌
                {manifest ? ` · ${Object.keys(manifest.variants).length} 个变体` : ''}
              </span>
            </p>
            {verdict.errors.length > 0 ? (
              <ul className="theme-publish__issues theme-publish__issues--bad">
                {verdict.errors.map((msg) => (
                  <li key={msg}>{msg}</li>
                ))}
              </ul>
            ) : null}
            {verdict.warnings.length > 0 ? (
              <ul className="theme-publish__issues theme-publish__issues--warn">
                {verdict.warnings.map((msg) => (
                  <li key={msg}>{msg}</li>
                ))}
              </ul>
            ) : null}
            {!previewUrls.card ? (
              <p className="notice notice--danger">
                还差一张市场卡片图（preview.png，800×500、≤400KB）。市场里每套主题都要有缩略图，
                没有图不能上架 —— 把它放回压缩包重传，或者在上面「市场卡片图」那里单独补一张。
              </p>
            ) : null}
            <Button
              disabled={!verdict.ok || busy === 'publish' || !previewUrls.card || uploading !== null}
              onClick={() => void onPublish()}
            >
              {busy === 'publish' ? '发布中…' : '发布并上架'}
            </Button>
          </div>
        ) : null}
      </Card>

      <Card
        title="已发布的主题"
        description="下架只影响能不能再买：已经订阅的客户会一直用到本期结束。内置四套不在这个列表里，它们跟着代码走。"
      >
        {list.length === 0 ? (
          <p className="muted">还没有通过后台发布过主题。</p>
        ) : (
          <div className="theme-grid">
            {list.map((item) => (
              <div key={item.name} className="theme-card theme-card--static">
                <span className="theme-card__name">
                  {item.label.zh}
                  <Badge tone={item.status === 'published' ? 'success' : 'neutral'}>
                    {item.status === 'published' ? '已上架' : '已下架'}
                  </Badge>
                </span>
                <span className="theme-card__desc">
                  {item.name} · v{item.version} · {item.variantCount} 个变体
                </span>
                <span className="theme-card__meta">
                  {item.monthlyCents > 0
                    ? `${formatMoney(item.monthlyCents, 'CNY', 'zh-CN')}/月`
                    : '免费主题'}
                </span>
                <div className="market-card__actions">
                  <Button
                    variant="ghost"
                    disabled={busy === item.name}
                    onClick={() => void onToggleStatus(item)}
                  >
                    {item.status === 'published' ? '下架' : '重新上架'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </section>
  );
}
